import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RequireAuth, RequirePage } from '@/auth/guards';
import { ROUTE_ROLES, type RouteKey } from '@/auth/rbac';
import { AppShell } from '@/app/layout/AppShell';
import { ROUTES, FORBIDDEN_PATH, START_PATH } from '@/app/routes';
import { ForbiddenPage } from '@/app/pages/ForbiddenPage';
import { RouteErrorPage } from '@/app/pages/RouteErrorPage';
import { LoginPage } from '@/features/login/pages/LoginPage';
import { DashboardPage } from '@/features/dashboard/pages/DashboardPage';
import { UserListPage } from '@/features/users/pages/UserListPage';
import { UserDetailPage } from '@/features/users/pages/UserDetailPage';
import { UserModerationHistoryPage } from '@/features/users/pages/UserModerationHistoryPage';
import { AdminListPage } from '@/features/admins/pages/AdminListPage';
import { AdminDetailPage } from '@/features/admins/pages/AdminDetailPage';
import { SystemPermissionsPage } from '@/features/system-permissions/pages/SystemPermissionsPage';
import { ContentListPage } from '@/features/content/pages/ContentListPage';
import { ContentDetailPage } from '@/features/content/pages/ContentDetailPage';
import { HumanReviewPage } from '@/features/content/pages/HumanReviewPage';
import { FrontierReviewPage } from '@/features/content/pages/FrontierReviewPage';
import { AutoRejectPage } from '@/features/content/pages/AutoRejectPage';
import { CreateTestPostPage } from '@/features/content/pages/CreateTestPostPage';
import { PipelineTestListPage } from '@/features/content/pages/PipelineTestListPage';
import { ModerationListPage } from '@/features/moderation/pages/ModerationListPage';
import { ModerationDetailPage } from '@/features/moderation/pages/ModerationDetailPage';
import { ModerationRulesPage } from '@/features/moderation/pages/ModerationRulesPage';
import { ReportedPostsPage } from '@/features/moderation/pages/ReportedPostsPage';
import { ModerationDashboardPage } from '@/features/moderation/pages/ModerationDashboardPage';
import { EffectPackReviewPage } from '@/features/effect-packs/pages/EffectPackReviewPage';
import { TfxCreatePage } from '@/features/tfx/pages/TfxCreatePage';
import { MyTfxDraftsPage } from '@/features/tfx/pages/MyTfxDraftsPage';
import { TfxReviewPage } from '@/features/tfx/pages/TfxReviewPage';
import { EffectsHubLayout } from '@/features/tfx/pages/EffectsHubLayout';
import { EffectCatalogPage } from '@/features/media/pages/EffectCatalogPage';
import { TransitionCatalogPage } from '@/features/media/pages/TransitionCatalogPage';
import { StickerLibraryPage } from '@/features/media/pages/StickerLibraryPage';
import { FinancePage } from '@/features/finance/pages/FinancePage';
import { EngagementPage } from '@/features/engagement/pages/EngagementPage';
import { CampaignPage } from '@/features/campaign/pages/CampaignPage';
import { PulsePage } from '@/features/pulse/pages/PulsePage';

// A protected route element gated by effective page permissions (fallback: ROUTE_ROLES seed).
const gated = (key: RouteKey, el: ReactNode): ReactNode => (
  <RequirePage page={key} fallbackRoles={ROUTE_ROLES[key]}>
    {el}
  </RequirePage>
);

export const router = createBrowserRouter([
  { path: ROUTES.login, element: <LoginPage /> },

  // Pulse: full-screen, no AppShell sidebar — but requires admin auth.
  {
    path: ROUTES.pulse,
    element: (
      <RequireAuth>
        <PulsePage />
      </RequireAuth>
    ),
  },

  {
    // Authenticated shell (RequireAuth used as a children-wrapper — CONTRACTS.md §9).
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to={START_PATH} replace /> },
      { path: FORBIDDEN_PATH, element: <ForbiddenPage /> },

      { path: ROUTES.dashboard, element: gated('dashboard', <DashboardPage />) },

      { path: ROUTES.users, element: gated('users', <UserListPage />) },
      {
        path: 'users/:userId/moderation-history',
        element: gated('users', <UserModerationHistoryPage />),
        errorElement: <RouteErrorPage />,
      },
      {
        path: 'users/:userId',
        element: gated('users', <UserDetailPage />),
        errorElement: <RouteErrorPage />,
      },

      { path: ROUTES.admins, element: gated('admins', <AdminListPage />) },
      {
        path: 'admins/:userId',
        element: gated('admins', <AdminDetailPage />),
        errorElement: <RouteErrorPage />,
      },
      {
        path: ROUTES.systemPermissions,
        element: gated('system-permissions', <SystemPermissionsPage />),
      },

      { path: ROUTES.content, element: gated('content', <ContentListPage />) },
      {
        path: ROUTES.humanReview,
        element: gated('content', <HumanReviewPage />),
      },
      {
        path: ROUTES.frontierReview,
        element: gated('content', <FrontierReviewPage />),
      },
      {
        path: ROUTES.autoReject,
        element: gated('content', <AutoRejectPage />),
      },
      {
        path: 'content/:contentId',
        element: gated('content', <ContentDetailPage />),
        errorElement: <RouteErrorPage />,
      },

      { path: ROUTES.moderation, element: gated('moderation', <ModerationListPage />) },
      {
        path: ROUTES.moderationDashboard,
        element: gated('moderation', <ModerationDashboardPage />),
      },
      { path: ROUTES.moderationReports, element: gated('moderation', <ReportedPostsPage />) },
      { path: ROUTES.pipelineTestPosts, element: gated('moderation', <PipelineTestListPage />) },
      { path: ROUTES.createTestPost, element: gated('moderation', <CreateTestPostPage />) },
      { path: ROUTES.moderationRules, element: gated('moderation', <ModerationRulesPage />) },
      {
        path: 'moderation/:moderationId/:type',
        element: gated('moderation', <ModerationDetailPage />),
        errorElement: <RouteErrorPage />,
      },

      // Media: stickers and transitions are flat pages; effects keep their own
      // tab hub, so there is deliberately no /media tab bar to nest inside.
      // Land on Effects, not Stickers: before Media existed, "Effects" was a
      // top-level entry that opened the Create tab, and that is the daily
      // workflow. Stickers and Transitions are curation, visited on purpose.
      { path: ROUTES.media, element: <Navigate to={ROUTES.effects} replace /> },
      { path: ROUTES.mediaStickers, element: gated('media-stickers', <StickerLibraryPage />) },
      {
        path: ROUTES.mediaTransitions,
        element: gated('media-transitions', <TransitionCatalogPage />),
      },
      {
        path: ROUTES.effects,
        element: gated('effects', <EffectsHubLayout />),
        children: [
          { index: true, element: <Navigate to={ROUTES.tfxCreate} replace /> },
          { path: 'packs', element: gated('effect-packs', <EffectPackReviewPage />) },
          { path: 'create', element: gated('tfx-create', <TfxCreatePage />) },
          { path: 'drafts', element: gated('tfx-drafts', <MyTfxDraftsPage />) },
          { path: 'review', element: gated('tfx-review', <TfxReviewPage />) },
          { path: 'catalog', element: gated('tfx-published', <EffectCatalogPage />) },
        ],
      },
      // Old bookmarks: the pre-hub layout, then the pre-Media /effects hub.
      { path: '/effect-packs', element: <Navigate to={ROUTES.effectPacks} replace /> },
      { path: '/tfx/drafts', element: <Navigate to={ROUTES.tfxDrafts} replace /> },
      { path: '/tfx/review', element: <Navigate to={ROUTES.tfxReview} replace /> },
      { path: '/tfx/published', element: <Navigate to={ROUTES.tfxPublished} replace /> },
      { path: '/effects', element: <Navigate to={ROUTES.effects} replace /> },
      { path: '/effects/packs', element: <Navigate to={ROUTES.effectPacks} replace /> },
      { path: '/effects/create', element: <Navigate to={ROUTES.tfxCreate} replace /> },
      { path: '/effects/drafts', element: <Navigate to={ROUTES.tfxDrafts} replace /> },
      { path: '/effects/review', element: <Navigate to={ROUTES.tfxReview} replace /> },
      { path: '/effects/catalog', element: <Navigate to={ROUTES.tfxPublished} replace /> },
      { path: ROUTES.campaigns, element: gated('campaigns', <CampaignPage />) },
      { path: ROUTES.finance, element: gated('finance', <FinancePage />) },
      { path: ROUTES.engagement, element: gated('engagement', <EngagementPage />) },

      { path: '*', element: <Navigate to={START_PATH} replace /> },
    ],
  },
]);
